import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface CreateAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

export function CreateAdminDialog({ open, onOpenChange, onSubmit, isLoading }: CreateAdminDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedGyms, setSelectedGyms] = useState<string[]>([]);
  const isMobile = useIsMobile();

  const { data: gyms = [] } = useQuery({
    queryKey: ['all-gyms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gyms')
        .select('id, name, city')
        .neq('id', '00000000-0000-0000-0000-000000000000')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedGyms.length === 0) {
      alert(t("createAdmin.selectTenantRequired"));
      return;
    }
    onSubmit({
      email,
      firstName,
      lastName,
      phone,
      gymIds: selectedGyms,
    });
  };

  const toggleGym = (gymId: string) => {
    setSelectedGyms(prev => 
      prev.includes(gymId) 
        ? prev.filter(id => id !== gymId)
        : [...prev, gymId]
    );
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("createAdmin.emailRequired")}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-background/50"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">{t("createAdmin.phone")}</Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="bg-background/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">{t("createAdmin.firstNameRequired")}</Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="bg-background/50"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">{t("createAdmin.lastNameRequired")}</Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="bg-background/50"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("createAdmin.assignTenants")} * ({t("createAdmin.selectedCount", { count: selectedGyms.length })})</Label>
        <div className="border rounded-lg p-4 max-h-60 overflow-y-auto bg-background/30">
          {gyms.map((gym) => (
            <div key={gym.id} className="flex items-center space-x-2 py-2">
              <Checkbox
                id={gym.id}
                checked={selectedGyms.includes(gym.id)}
                onCheckedChange={() => toggleGym(gym.id)}
              />
              <label
                htmlFor={gym.id}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
              >
                {gym.name} - {gym.city}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full md:w-auto">
          {t("common:cancel")}
        </Button>
        <Button type="submit" disabled={isLoading} className="w-full md:w-auto">
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("createAdmin.submit")}
        </Button>
      </div>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle>{t("createAdmin.title")}</DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-4">
            {formContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("createAdmin.title")}</DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
